'use client'

import { useQuery } from '@tanstack/react-query'
import { getCookie, setCookie } from 'cookies-next'
import { useAtomValue } from 'jotai'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { useAccountsFilteredBalances } from '@/src/queries/hooks/balances'
import { getInsightsQuery } from '@/src/queries/keys/insights'
import { getInsightsQueryHomepageOptions } from '@/src/services/insights'
import { walletKeyringAtom } from '@/src/stores/wallets/atoms'
import {
  SectionTabsTrigger,
  Tabs,
  TabsContent,
  TabsList,
} from '../ui/tabs/horizontal'
import { InsightsGrid } from './insights-grid'

const MIN_BALANCE_USD = 5

type ForYouSectionProps = {
  initialTab?: 'for-you' | 'following'
}

export function ForYouSection({ initialTab = 'for-you' }: ForYouSectionProps) {
  const [selectedTab, setSelectedTab] = useState<'for-you' | 'following'>(
    initialTab,
  )
  const [isBalancesLoading, setIsBalancesLoading] = useState(true)

  // Sync with cookie on mount
  useEffect(() => {
    const savedTab = getCookie('homepage-for-you-tab') as
      | 'for-you'
      | 'following'
      | undefined

    if (savedTab && (savedTab === 'for-you' || savedTab === 'following')) {
      setSelectedTab(savedTab)
    }
  }, [])

  const { data: curatedInsights = [], isPending: isCuratedPending } = useQuery(
    getInsightsQuery(getInsightsQueryHomepageOptions),
  )

  const { addresses } = useAtomValue(walletKeyringAtom)
  const isAnyWalletConnected = Object.values(addresses).length > 0
  const { balancesSummary: balances, totalBalance } =
    useAccountsFilteredBalances({
      minValueUsd: MIN_BALANCE_USD,
    })

  const tickersWithNonTrivialBalance = useMemo(
    () =>
      balances.flatMap((balance) => balance.summary.tickers).filter(Boolean),
    [balances],
  )

  const hasBalances =
    isAnyWalletConnected &&
    balances.length > 0 &&
    totalBalance > 0 &&
    tickersWithNonTrivialBalance.length > 0

  // Set up timeout for wallet + balance loading state
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsBalancesLoading(false)
    }, 3000) // 3 second timeout for both wallet connection and balance loading

    return () => clearTimeout(timeout)
  }, [])

  // Reset loading state when balances are detected
  useEffect(() => {
    if (hasBalances) {
      setIsBalancesLoading(false)
    }
  }, [hasBalances])

  const {
    data: followingInsights = [],
    isPending: isFollowingInsightsPending,
  } = useQuery({
    ...getInsightsQuery({
      _start: 0,
      _end: 7,
      _sort: 'updatedAt',
      _order: 'desc',
      tickers: tickersWithNonTrivialBalance.join(','),
    }),
    enabled: hasBalances,
  })

  const handleTabChange = (value: string) => {
    if (value === 'for-you' || value === 'following') {
      setSelectedTab(value)
      setCookie('homepage-for-you-tab', value, {
        maxAge: 60 * 60 * 24 * 365, // 1 year
        path: '/',
      })
    }
  }

  return (
    <section className='w-full'>
      <Tabs value={selectedTab} onValueChange={handleTabChange}>
        <TabsList className='flex flex-row justify-start max-w-4xl mx-auto py-1'>
          <SectionTabsTrigger value='for-you' title='For you'>
            For you
          </SectionTabsTrigger>
          <SectionTabsTrigger value='following' title='Following'>
            Following
          </SectionTabsTrigger>
        </TabsList>
        <TabsContent value='for-you' className='outline-none'>
          <InsightsGrid
            insights={curatedInsights}
            isLoading={isCuratedPending}
          />
        </TabsContent>
        <TabsContent value='following' className='outline-none'>
          {hasBalances || isBalancesLoading ? (
            <InsightsGrid
              insights={followingInsights}
              isLoading={
                (isFollowingInsightsPending || isBalancesLoading) &&
                !hasBalances
              }
            />
          ) : (
            <div className='max-w-4xl mx-auto w-full flex flex-col justify-center items-center text-center gap-6'>
              <Image
                src='/assets/cactus.png'
                alt='Cactus in desert'
                width={240}
                height={240}
              />
              <div className='flex flex-col gap-3'>
                <h3 className='text-4xl text-primary font-serif font-extralight'>
                  It&apos;s looking <em className='font-italic'>quiet</em>{' '}
                  today&hellip;
                </h3>
                <span className='text-secondary'>
                  Insights will show here for tokens you follow, with balances
                  over $5.
                </span>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </section>
  )
}
